import { urlFor } from "@/utils/client";
import { IQuestionFlow } from "@/utils/types";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Button } from "./button";

const cardTransition = {
  type: "spring" as const,
  stiffness: 260,
  damping: 30,
  mass: 0.6,
};

export function QuestionFlow(props: IQuestionFlow) {
  const questions = (props.questions ?? []).map((item) =>
    typeof item === "string" ? { question: item } : item,
  );
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<boolean[]>([]);

  const step = answers.length;
  // Any "no" ends the flow immediately in the failed state.
  const hasNo = answers.some((answer) => !answer);
  const done =
    questions.length > 0 && (hasNo || step === questions.length);
  const allYes = done && !hasNo;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (questions.length === 0) return null;

  const answer = (value: boolean) => setAnswers((prev) => [...prev, value]);
  const restart = () => setAnswers([]);
  const openFlow = () => {
    setAnswers([]);
    setOpen(true);
  };

  return (
    <>
      {/* full-screen intro */}
      <div className="relative w-full h-[70vh] md:h-[90vh] bg-darkGrey overflow-hidden flex items-end justify-center">
        {props.image?.image && (
          <Image
            fill
            sizes="100vw"
            alt={props.image?.caption ?? ""}
            src={urlFor(props.image.image).url()}
            className="object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="relative z-10 flex flex-col items-center component-gap max-w-3xl px-4 pb-16 md:pb-24 text-white text-center">
          <p className="heading">{props.title}</p>
          {props.description && <p className="opacity-90">{props.description}</p>}
          <button
            onClick={openFlow}
            className="border border-white font-display text-white py-2 px-6 hover:scale-105 transition-all active:scale-100"
          >
            {props.buttonLabel || "Doe de test"}
          </button>
        </div>
      </div>

      {/* overlay with the question flow */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={cardTransition}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white text-darkGrey w-full max-w-4xl max-h-[85dvh] flex flex-col"
            >
              {/* Outside the scrolling area so it stays reachable on mobile. */}
              <button
                onClick={() => setOpen(false)}
                aria-label="Sluiten"
                className="absolute top-3 right-3 z-10 w-11 h-11 rounded-full bg-white shadow-md font-display text-xl flex items-center justify-center hover:scale-110 transition-all active:scale-100"
              >
                ✕
              </button>
              <div className="overflow-y-auto p-8 pt-14 md:p-16 flex flex-col items-center gap-8 text-center">
                {!done && (
                  <>
                    <p className="font-display text-lg md:text-xl text-balance">
                      {`Vraag ${step + 1}`}
                    </p>

                    {/* progress */}
                    <div className="w-full max-w-md h-1.5 bg-darkGrey/10 rounded-full overflow-hidden">
                      <motion.div
                        animate={{
                          width: `${(step / questions.length) * 100}%`,
                        }}
                        transition={cardTransition}
                        className="h-full bg-darkGrey rounded-full"
                      />
                    </div>
                  </>
                )}

                {/* question / result card */}
                <div className="w-full min-h-[180px] md:min-h-[200px] flex items-center justify-center overflow-x-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    {!done ? (
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -40 }}
                        transition={cardTransition}
                        className="flex flex-col items-center gap-8"
                      >
                        {questions[step]?.icon?.image && (
                          <Image
                            width={160}
                            height={160}
                            alt={questions[step].icon?.caption ?? ""}
                            src={urlFor(questions[step].icon!.image).url()}
                            className="w-16 h-16 md:w-20 md:h-20 object-contain"
                          />
                        )}
                        <p className="font-display text-2xl md:text-4xl text-balance">
                          {questions[step]?.question}
                        </p>
                        {questions[step]?.subtext && (
                          <p className="opacity-80 max-w-xl">
                            {questions[step].subtext}
                          </p>
                        )}
                        <div className="flex justify-center gap-4">
                          <button
                            onClick={() => answer(true)}
                            className="bg-darkGrey font-display text-white py-2 px-8 hover:scale-105 transition-all active:scale-100"
                          >
                            Ja
                          </button>
                          <button
                            onClick={() => answer(false)}
                            className="border border-darkGrey/40 font-display text-darkGrey py-2 px-8 hover:scale-105 hover:border-darkGrey transition-all active:scale-100"
                          >
                            Nee
                          </button>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="result"
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -40 }}
                        transition={cardTransition}
                        className="w-full flex flex-col md:flex-row items-center gap-6 md:gap-10"
                      >
                        {(() => {
                          const image = allYes
                            ? props.successImage
                            : props.failImage;
                          if (!image?.image) return null;
                          // Capped so text and button stay in view without scrolling.
                          return (
                            <div className="relative w-full md:w-1/2 shrink-0 aspect-[4/3] max-h-[30vh] md:max-h-[45vh] overflow-hidden">
                              <Image
                                fill
                                sizes="(max-width: 768px) 100vw, 28rem"
                                alt={image.caption ?? ""}
                                src={urlFor(image.image).url()}
                                className="object-cover"
                              />
                            </div>
                          );
                        })()}
                        <div className="flex flex-col items-center md:items-start gap-4 md:gap-6 text-center md:text-left">
                          <p className="font-display text-2xl md:text-4xl text-balance">
                            {allYes ? props.successText : props.failText}
                          </p>
                          {(() => {
                            const description = allYes
                              ? props.successDescription
                              : props.failDescription;
                            if (!description) return null;
                            return (
                              <p className="opacity-80 max-w-xl whitespace-pre-line">
                                {description}
                              </p>
                            );
                          })()}
                          {(() => {
                            const button = allYes
                              ? props.successButton
                              : props.failButton;
                            if (!button?.link || !button?.label) return null;
                            return (
                              <Button
                                link={button.link}
                                label={button.label}
                                backgroundColor={props.backgroundColor}
                                accentColor={props.accentColor}
                              />
                            );
                          })()}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* caption / restart */}
                <div className="flex items-center gap-6 text-sm italic min-h-[20px]">
                  {!done ? (
                    props.overlayText && (
                      <p className="opacity-60">{props.overlayText}</p>
                    )
                  ) : (
                    <button
                      onClick={restart}
                      className="opacity-60 hover:opacity-100 hover:underline transition-opacity"
                    >
                      Opnieuw beginnen
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
